// app/frontend/components/inbox/SystemMessage.tsx

import { Check, RotateCcw, Archive, ArchiveRestore, Ban, Mail, UserCheck, UserX, Star } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatTime } from '@/lib/dateUtils'
import type { Message } from '@/types/conversation'

interface SystemMessageProps {
  message: Message
}

type SystemMessageKind =
  | 'resolved'
  | 'reopened'
  | 'archived'
  | 'unarchived'
  | 'spam'
  | 'unspam'
  | 'follow_up_sent'
  | 'assigned'
  | 'unassigned'
  | 'transcript_sent'
  | 'csat_collected'

const STYLES: Record<SystemMessageKind, { icon: LucideIcon; className: string }> = {
  resolved:        { icon: Check,          className: 'bg-success/10 text-success' },
  reopened:        { icon: RotateCcw,      className: 'bg-muted text-muted-foreground' },
  archived:        { icon: Archive,        className: 'bg-muted text-muted-foreground' },
  unarchived:      { icon: ArchiveRestore, className: 'bg-muted text-muted-foreground' },
  spam:            { icon: Ban,            className: 'bg-muted text-muted-foreground' },
  unspam:          { icon: RotateCcw,      className: 'bg-muted text-muted-foreground' },
  follow_up_sent:  { icon: Mail,           className: 'bg-muted text-muted-foreground' },
  assigned:        { icon: UserCheck,      className: 'bg-primary/10 text-primary' },
  unassigned:      { icon: UserX,          className: 'bg-muted text-muted-foreground' },
  transcript_sent: { icon: Mail,           className: 'bg-muted text-muted-foreground' },
  csat_collected:  { icon: Star,           className: 'bg-warning/10 text-warning' },
}

function resolveKind(message: Message): SystemMessageKind | null {
  const kind = message.metadata?.kind
  if (typeof kind === 'string' && kind in STYLES) {
    return kind as SystemMessageKind
  }
  // Fallback for pre-refactor messages that lack metadata.kind.
  // Order matters: 'unarchived' must be checked before 'archived' and
  // 'unmarked as spam' before 'marked as spam', since the shorter phrase
  // is a substring of the longer one.
  const content = message.content.toLowerCase()
  if (content.includes('resolved')) return 'resolved'
  if (content.includes('reopened')) return 'reopened'
  if (content.includes('unarchived')) return 'unarchived'
  if (content.includes('archived')) return 'archived'
  if (content.includes('unmarked as spam')) return 'unspam'
  if (content.includes('marked as spam')) return 'spam'
  if (content.includes('assigned to')) return 'assigned'
  if (content.includes('unassigned')) return 'unassigned'
  if (content.includes('transcript sent')) return 'transcript_sent'
  return null
}

export function SystemMessage({ message }: SystemMessageProps) {
  const kind = resolveKind(message)
  const style = kind ? STYLES[kind] : null
  const Icon = style?.icon
  const performedBy = message.metadata?.performed_by as string | undefined

  return (
    <div className="flex justify-center my-2">
      <div
        className={cn(
          "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm",
          style?.className ?? 'bg-muted text-muted-foreground'
        )}
      >
        {Icon && <Icon className="size-4" />}
        <span>{message.content}</span>
        {performedBy && (
          <span className="text-xs opacity-70">by {performedBy}</span>
        )}
        <span className="text-xs opacity-50">{formatTime(message.created_at)}</span>
      </div>
    </div>
  )
}

export default SystemMessage
